import { getLoggerContext } from './LoggerContext';
import { getCorrelationId as getSessionCorrelationId } from '../correlation/correlationId';
import { LoggerConfig, LOG_LEVEL_ORDER } from './LoggerConfig';
import { ConsoleTransport } from './transports/ConsoleTransport';
import { LogTransport } from './transports/LogTransport';
import { AutoPopulatedEventFields, LogEvent, LogLevel } from './types';

/**
 * Caller-facing event input. The {@link Logger} fills the fields listed in
 * {@link AutoPopulatedEventFields} automatically, so callers only provide
 * the category-specific fields.
 */
export type LogEventInput = Omit<LogEvent, AutoPopulatedEventFields>;

/**
 * Central front-end logger.
 *
 * Responsibilities:
 * - Filter events by the configured {@link LoggerConfig.minLevel}.
 * - Auto-populate the {@link AutoPopulatedEventFields} on every event
 *   (including `level`, which is derived from the method called).
 * - Fan the fully-populated event out to every configured transport,
 *   isolating transport failures so one bad sink never breaks the others.
 */
export class Logger {
    private readonly config: LoggerConfig;

    private readonly transports: LogTransport[];

    public constructor(config: LoggerConfig, transports: LogTransport[]) {
        this.config = config;
        this.transports = transports;
    }

    public debug(event: LogEventInput): void {
        this._emit('DEBUG', event);
    }

    public info(event: LogEventInput): void {
        this._emit('INFO', event);
    }

    public warn(event: LogEventInput): void {
        this._emit('WARN', event);
    }

    public error(event: LogEventInput): void {
        this._emit('ERROR', event);
    }

    private _emit(level: LogLevel, event: LogEventInput): void {
        if (!this.config.enabled) {
            return;
        }
        if (LOG_LEVEL_ORDER[level] < LOG_LEVEL_ORDER[this.config.minLevel]) {
            return;
        }
        // `as LogEvent` is required because spreading a discriminated-union
        // value (`LogEventInput`) collapses the discriminator, and `satisfies`
        // cannot reconstruct it. The shape is enforced by `LogEventInput` on
        // the public methods and by every base field being explicitly set below.
        const { sessionId, userId, correlationId: contextCorrelationId } = getLoggerContext();
        // `withCorrelation()` sets a scoped ID that wins for the duration of
        // its block (used to tie a single user flow together across async
        // hops). Outside that scope, fall back to the per-session ID owned
        // by `services/correlation/correlationId.ts` so every log emitted
        // after login shares the same ID as the `X-Correlation-Id` header
        // sent to the backend (see `app/api/baseApi.ts prepareHeaders`).
        const correlationId = contextCorrelationId || getSessionCorrelationId();
        // `browser` / `os` are stamped only when the config provides them so
        // events keep the same shape on runtimes that cannot resolve them
        // (e.g. React Native without a UA string).
        const fullEvent = {
            ...event,
            level,
            timestamp: new Date().toISOString(),
            appName: this.config.appName,
            appVersion: this.config.appVersion,
            environment: this.config.environment,
            sessionId,
            userId,
            correlationId,
            ...(this.config.browser !== undefined && { browser: this.config.browser }),
            ...(this.config.os !== undefined && { os: this.config.os }),
        } as LogEvent;
        for (const transport of this.transports) {
            try {
                transport.send(fullEvent);
            } catch {
                // Transports must not throw, but defend against misbehaving ones
                // so a single bad transport cannot break the others.
            }
        }
    }
}

/**
 * Convenience factory that wires a {@link Logger} with a sensible default
 * transport list (a single {@link ConsoleTransport}).
 */
export const createLogger = (config: LoggerConfig, transports: LogTransport[] = [new ConsoleTransport()]): Logger =>
    new Logger(config, transports);

/**
 * App-level shared logger, populated once during platform bootstrap.
 *
 * Consumed by cross-platform code that lives in `packages/common` (e.g.
 * `app/api/baseApi.ts`) so it can emit events without importing the
 * platform-specific singleton from `packages/web` or `packages/mobile`.
 * Left `undefined` until a platform calls {@link setSharedLogger}, so
 * pre-bootstrap calls (SSR, tests, contract fixtures) become no-ops
 * rather than crashing.
 */
let sharedLogger: Logger | undefined;

/**
 * Register the app-level shared logger. Call once from the web / mobile
 * logger bootstrap (`services/logger.ts`). Subsequent calls overwrite the
 * previous value, which is only useful in tests.
 */
export const setSharedLogger = (loggerInstance: Logger): void => {
    sharedLogger = loggerInstance;
};

/**
 * Read the app-level shared logger, or `undefined` when no platform has
 * bootstrapped one yet. Callers MUST handle `undefined` so a missing
 * bootstrap never crashes the API hot path or auth flow.
 */
export const getSharedLogger = (): Logger | undefined => sharedLogger;

/**
 * Clear the app-level shared logger, restoring the pre-bootstrap state.
 * Primarily used by test suites to guarantee no state leaks across
 * `describe` blocks; production code paths should not need to call this.
 */
export const clearSharedLogger = (): void => {
    sharedLogger = undefined;
};
